// suelta-api.js
//
// #1887 · jornada de 12 h con botón + jornadas sueltas (spec `.claude/specs/jornada-suelta/`, T23).
// Cliente de los tres endpoints nuevos y los formatos que comparten la banda de /turno, el diálogo de
// confirmación y la compra. TODA la regla vive en el backend (quién puede activar, qué opciones hay, qué hora
// de término tendría cada una, cuánto cuesta, hasta cuántas se pueden comprar): acá solo se pinta lo que
// responde, nunca se recalcula plata ni horas (memoria `feedback_logica_en_el_back_para_que_no_copien`).
//
//   GET  /api/billing/me/jornada/            estado de la ventana, `opciones_activar`, saldo de sueltas (R28, R68)
//   POST /api/billing/me/jornada/activar/    {opcion, termina_en_visto} -> la ventana abierta (R45)
//   GET  /api/billing/me/suelta/             la oferta de la propia cuenta, con las órdenes (R29)
//   POST /api/billing/me/suelta/             {tipo, cantidad, orden_hash} -> {suelta_id, url_pago} (R34)
import getClient from "./get-client";

export const URL_JORNADA = "/api/billing/me/jornada/";
export const URL_ACTIVAR = "/api/billing/me/jornada/activar/";
export const URL_SUELTA = "/api/billing/me/suelta/";

// Los tres 403 nuevos del corte de IA (R47, R49, R54, R62). NO son codes que bloqueen la app: ni
// `SuscripcionGate.CODES_QUE_BLOQUEAN` ni `ProfileRequiredGate` los suman, a propósito (leer, copiar, editar y
// guardar siguen siempre, R52). Los atiende `JornadaAvisosGate` con un aviso, nunca con una pantalla de bloqueo.
// Cubre R62 — .claude/specs/jornada-suelta/
export const CODES_CORTE_IA = ["sin_jornadas", "requiere_boton", "ventana_terminada"];

/** ¿Este error de axios es uno de los 403 del corte de IA? Lo usan las piezas para NO pintar su error técnico. */
export const esCorteDeJornada = (err) =>
  err?.response?.status === 403 && CODES_CORTE_IA.includes(err?.response?.data?.code);

/** Para el `catch` de una pieza que recibe un 403: ¿era un corte de jornadas? (acepta un code suelto o un error). */
export const esCodeDeCorte = (code) => CODES_CORTE_IA.includes(code);

// Eventos propios (el bloqueo de acceso ya viaja por `smartdx:acceso-bloqueado`, que emite get-client).
export const EVENTO_JORNADA_CAMBIO = "smartdx:jornada-changed";
export const EVENTO_ABRIR_CONFIRMAR = "smartdx:jornada-confirmar";
export const EVENTO_ABRIR_COMPRA = "smartdx:suelta-comprar";

// `opcion` (plan | 12h | 24h) salta la elección y va directo a la confirmación de esa opción (R70: «Activar otra»).
export const abrirConfirmacionJornada = (opcion) =>
  window.dispatchEvent(new CustomEvent(EVENTO_ABRIR_CONFIRMAR, { detail: { opcion: typeof opcion === "string" ? opcion : null } }));

// La pantalla `jornadas-agotadas` avisa cuando se abre o se cierra: mientras está abierta, la banda de /turno de atrás NO
// repite sus botones («Sumar jornada adicional» y «Subir de plan»), que quedan solo en el diálogo (Sebastián, 31-oct-2026).
// Con el diálogo cerrado la banda sigue como siempre.
export const EVENTO_AGOTADAS = "smartdx:jornadas-agotadas";
let agotadasAbiertas = false;
export const hayAgotadasAbiertas = () => agotadasAbiertas;
export const avisarAgotadas = (abierta) => {
  if (agotadasAbiertas === Boolean(abierta)) return;
  agotadasAbiertas = Boolean(abierta);
  window.dispatchEvent(new Event(EVENTO_AGOTADAS));
};
export const abrirCompraSuelta = () => window.dispatchEvent(new Event(EVENTO_ABRIR_COMPRA));
export const avisarCambioDeJornada = () => window.dispatchEvent(new Event(EVENTO_JORNADA_CAMBIO));

export const pedirJornada = async () => (await getClient().get(URL_JORNADA)).data;
export const pedirOferta = async () => (await getClient().get(URL_SUELTA)).data;

export const activarJornada = async ({ opcion, terminaEnVisto }) =>
  (await getClient().post(URL_ACTIVAR, { opcion, termina_en_visto: terminaEnVisto })).data;

// Devuelve `{ok:true, data}` o `{ok:false, code, status, data}`: quien llama decide el copy (como `useCheckout.iniciar`).
export const comprarSuelta = async ({ tipo, cantidad, ordenHash }) => {
  try {
    const res = await getClient().post(URL_SUELTA, { tipo, cantidad, orden_hash: ordenHash });
    return { ok: true, data: res.data };
  } catch (err) {
    return {
      ok: false,
      code: err?.response?.data?.code || null,
      status: err?.response?.status || null,
      data: err?.response?.data || null,
    };
  }
};

// ── Formatos (hora de Chile, igual que el servidor) ─────────────────────────────────────────────
export const TZ = "America/Santiago";

export const hora = (iso) =>
  new Intl.DateTimeFormat("es-CL", { timeZone: TZ, hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(iso));

const diaClave = (d) =>
  new Intl.DateTimeFormat("en-CA", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit" }).format(d);

export const fechaLarga = (iso) =>
  new Intl.DateTimeFormat("es-CL", { timeZone: TZ, weekday: "long", day: "numeric", month: "long" })
    .format(new Date(iso))
    .replace(",", "");

/** "de hoy" / "de mañana" / "del jueves 5 de noviembre": compara el día en hora de Chile. */
export const cuando = (iso, ahora) => {
  const fin = diaClave(new Date(iso));
  if (fin === diaClave(ahora)) return "de hoy";
  const manana = new Date(ahora.getTime() + 24 * 3600 * 1000);
  if (fin === diaClave(manana)) return "de mañana";
  return `del ${fechaLarga(iso)}`;
};

/** "29 de diciembre" desde una fecha `AAAA-MM-DD` (la que manda el back, sin hora: se lee al mediodía de Chile). */
export const diaMes = (fecha) =>
  fecha
    ? new Intl.DateTimeFormat("es-CL", { timeZone: TZ, day: "numeric", month: "long" }).format(new Date(`${fecha}T12:00:00-03:00`))
    : "";

export const diaMesAnio = (fecha) =>
  fecha
    ? new Intl.DateTimeFormat("es-CL", { timeZone: TZ, day: "numeric", month: "long", year: "numeric" }).format(
        new Date(`${fecha}T12:00:00-03:00`),
      )
    : "";

/** Diferencia de offset de Chile entre dos instantes, en minutos (cambio de hora de abril y septiembre). */
export const offsetMin = (d) => {
  const p = new Intl.DateTimeFormat("en-US", { timeZone: TZ, timeZoneName: "longOffset" }).formatToParts(d);
  const m = /GMT([+-])(\d{2}):(\d{2})/.exec(p.find((x) => x.type === "timeZoneName").value);
  return m ? (m[1] === "-" ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3])) : 0;
};

export const pesos = (n) => `$${Number(n).toLocaleString("es-CL")}`;

/**
 * "12 h" / "24 h" con espacio que no se parte: a 390 px «24 / h» no se corta en dos líneas (Sebastián, 31-oct-2026).
 * Cubre los textos de precio de la compra y de `jornadas-agotadas`.
 */
export const horasTxt = (tipo) => (tipo === "24h" ? "24\u00a0h" : "12\u00a0h");
/** "jornada adicional de 12 h" (D15: en pantalla nunca "turno de 12 h"; nombre «Jornada adicional» decidido el 31-oct-2026). */
export const nombreTipo = (tipo) => `jornada adicional de ${horasTxt(tipo)}`;
export const nombreTipoPlural = (tipo, n) =>
  `${n} ${nombreTipo(tipo).replace("jornada adicional", n === 1 ? "jornada adicional" : "jornadas adicionales")}`;
/** "12 h $5.000 · 24 h $10.000": los precios de la oferta, tal como los manda el backend. */
export const preciosTxt = (oferta) =>
  (oferta?.productos || []).map((p) => `${horasTxt(p.tipo)} ${pesos(p.precio_bruto)}`).join(" · ");

/** El tipo de una opción de `opciones_activar`: `plan` y `12h` duran 12 h; `24h` dura 24 h. */
export const horasDe = (opcion) => (opcion === "24h" ? 24 : 12);

// ── Lectura del estado de la jornada ────────────────────────────────────────────────────────────
// El estado de la banda se decide comparando instantes (regla de `frontend.md`: un vencimiento nunca se decide con días redondeados).
// "antes" = tarjeta · "activo" · "por_terminar" · "terminado" (con saldo) · "sin_saldo"; null = la banda no se dibuja.
export const estadoDe = (j, ahora) => {
  if (!j || typeof j !== "object") return null;
  // Revisor del front, falla 2: `avisos_visibles` en falso (exentos, Max+ y planes sin tope, cuentas en prueba, `JORNADAS_MODO=sombra`)
  // = la banda NO existe en NINGÚN estado. A esas cuentas el gate no les corta la IA y «tu jornada termina... sin IA» sería falso.
  // El backend ya lo manda falso para ellas (`billing/jornadas.py:estado_para_medico`); acá se respeta sin excepción.
  if (j.avisos_visibles !== true) return null;
  if (j.abierta && j.cierra_en && j.avisos_visibles) {
    if (new Date(j.cierra_en) <= ahora) return "terminado";
    return j.aviso_cierre_desde && new Date(j.aviso_cierre_desde) <= ahora ? "por_terminar" : "activo";
  }
  if (j.ia_permitida === false && j.avisos_visibles) {
    return j.motivo_activar === "sin_saldo" || j.motivo_ia === "sin_saldo" ? "sin_saldo" : "terminado";
  }
  return j.puede_activar ? "antes" : null;
};

/** Las opciones que puede activar: las decide el backend (R46). Sin la clave, la única opción es la del plan. */
export const opcionesDe = (j) =>
  Array.isArray(j?.opciones_activar) && j.opciones_activar.length
    ? j.opciones_activar
    : j?.termina_si_activas
      ? [{ opcion: "plan", termina_si_activas: j.termina_si_activas }]
      : [];

/** Saldo del plan (en jornadas de 12 h, o sea medias) y de sueltas por tipo. */
export const saldoDe = (j) => {
  const plan = Number(j?.periodo?.incluidas || 0) * 2;
  const usadas = Number(j?.periodo?.usadas_medias || 0);
  const delPlan = Math.max(plan - usadas, 0);
  const porTipo = j?.suelta?.saldo || {};
  const n12 = Number(porTipo["12h"] || 0);
  const n24 = Number(porTipo["24h"] || 0);
  return { plan, delPlan, n12, n24, hayGuardadas: n12 + n24 > 0 };
};

const guardadasTxt = (n12, n24) =>
  [n12 ? `${n12} de 12\u00a0h` : "", n24 ? `${n24} de 24\u00a0h` : ""].filter(Boolean).join(" y ");
export const guardadas = guardadasTxt;

/** "Te quedan 3 de 6 y guardadas: 2 de 12 h": lo comprado va aparte del plan, si no daría "14 de 6". */
export const textoSaldo = (j) => {
  const { plan, delPlan, n12, n24, hayGuardadas } = saldoDe(j);
  if (hayGuardadas) {
    const g = `guardadas: ${guardadasTxt(n12, n24)}`;
    return delPlan > 0 ? `Te quedan ${delPlan} de ${plan} y ${g}` : `${g[0].toUpperCase()}${g.slice(1)}`;
  }
  return `Te quedan ${delPlan} de ${plan}`;
};
